import { XCircle } from "phosphor-react";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button } from "../../../../../components/Button";
import { DialogModal } from "../../../../../components/DialogModal";
import { PasswordInput } from "../../../../../components/FormElements/PasswordInput";
import { SearchInput } from "../../../../../components/SearchInput";
import { type Column } from "../../../../../components/Table";
import { Medias } from "../../../../../enumns/medias";
import { useMediaQuery } from "../../../../../hooks/useMediaQuery";
import { ResearcherForm } from "./components/ResearcherForm";
import { TableRowActions } from "../../components/TableRowActions";
import { TableWithInfiniteScroll } from "./components/TableWithInfiniteScroll";
import { TableWithManuallyPagination } from "./components/TableWithManuallyPagination";
import { type ResearcherDomain } from "../../../../../types/domain/researcher-domain";
import { type ResearcherPersistence } from "../../../../../types/persistence/researcher-persistence";
import { useResearcherCreateMutation } from "../../../../../services/hooks/researchers/mutations/useResearcherCreateMutation";
import { useResearcherRemoveMutation } from "../../../../../services/hooks/researchers/mutations/useResearcherRemoveMutation";
import { useResearcherEditMutation } from "../../../../../services/hooks/researchers/mutations/useResearcherEditMutation";
import { useResearcherUpdatePasswordMutation } from "../../../../../services/hooks/researchers/mutations/useResearcherUpdatePasswordMutation";
import { createResetPasswordSchema } from "../../../../../validators/resetPasswordSchema";
import { NavigationSelect } from "../../components/NavigationSelect";
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from "../../../../../constants/toast";
import { type FiltersDomain } from "../../../../../types/domain/filters-domain";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { Can, GuardContext } from "../../../../../guards/GuardContext";

type ResetPasswordFormValues = {
  password: string;
  passwordConfirmation: string;
};

const resetPasswordSchema = createResetPasswordSchema({
  password: {
    min: "Deve conter 8 caracteres, uma maiúscula, uma minúscula, um número e um caractere especial",
    matches:
      "Deve conter 8 caracteres, uma maiúscula, uma minúscula, um número e um caractere especial",
    identical: "Senhas devem ser idênticas",
    required: "Campo obrigatório",
  },
  passwordConfirmation: {
    identical: "Senhas devem ser idênticas",
    required: "Campo obrigatório",
  },
});

export const Researcher = () => {
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`);

  const queryClient = useQueryClient();
  const researcherCreationMutation = useResearcherCreateMutation();
  const researcherRemotionMutation = useResearcherRemoveMutation();
  const researcherEditionMutation = useResearcherEditMutation();
  const researcherUpdatePasswordMutation =
    useResearcherUpdatePasswordMutation();
  const ability = useContext(GuardContext);

  const [researcherToEdit, setResearcherToEdit] = useState();
  const [researcherToResetPassword, setResearcherToResetPassword] = useState<
    ResearcherDomain | undefined
  >(undefined);
  const [formModalIsOpen, setFormModalIsOpen] = useState(false);
  const [resetPasswordModalIsOpen, setResetPasswordModalIsOpen] =
    useState(false);
  const [filters, setFilters] = useState<FiltersDomain>({
    page: 1,
    take: 10,
    search: "",
    order: "DESC",
    sort: "createdAt",
  });

  const isFormInEditMode = Boolean(researcherToEdit);

  const {
    control,
    formState: resetPasswordFormState,
    handleSubmit: handleResetPasswordSubmit,
    reset: resetPasswordForm,
  } = useForm<ResetPasswordFormValues>({
    mode: "onChange",
    resolver: yupResolver(resetPasswordSchema),
  });

  const getCachedResearcherById = (id: number) => {
    const researchersFromCache: any = queryClient.getQueryData([
      "researchers",
      filters,
    ]);

    if (isMinScreenWidthMD) {
      return researchersFromCache.data.find(
        (researcher: ResearcherDomain) => researcher.id === id
      );
    }

    return researchersFromCache?.pages
      ?.reduce((acc: any, item: { data: ResearcherDomain[] }) => {
        return [...acc, ...item.data];
      }, [])
      .find((researcher: ResearcherDomain) => researcher.id === id);
  };

  const canCreateResearcher = ability.can("create", "Researcher");

  const columns = [
    {
      field: "id",
      isVisible: false,
    },
    {
      field: "name",
      headerName: "Nome",
      widthInPercentage: canCreateResearcher ? 40 : 80,
    },
    {
      field: "email",
      headerName: "E-mail",
      widthInPercentage: canCreateResearcher ? 50 : null,
    },
    canCreateResearcher
      ? {
          field: "actions",
          headerName: "Ações",
          widthInPercentage: 10,
          renderCell({ id }: { id: number }) {
            return (
              <TableRowActions
                tableContentType="Researcher"
                isRemovingRegister={researcherRemotionMutation.isLoading}
                alertMessage="Você está prestes a excluir um researcher. Deseja continuar?"
                onEdit={() => {
                  setResearcherToEdit(getCachedResearcherById(id));
                  setFormModalIsOpen(true);
                }}
                onResetPassword={() => {
                  setResearcherToResetPassword(getCachedResearcherById(id));
                  setResetPasswordModalIsOpen(true);
                }}
                onRemove={(successAction) =>
                  handleRemoveResearcher(id, successAction)
                }
              />
            );
          },
        }
      : null,
  ].filter(Boolean) as Column[];

  const createResearcher = (data: ResearcherPersistence) => {
    researcherCreationMutation.mutate(data, {
      onSuccess: () => {
        toast.success(
          "Researcher cadastrado com sucesso",
          TOAST_SUCCESS_DEFAULT_OPTIONS
        );

        setFormModalIsOpen(false);
        queryClient.invalidateQueries(["researchers", filters]);
      },
      onError: () => {
        toast.error("Erro ao cadastrar researcher", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          closeButton: false,
          className: "bg-red-50",
          icon: (
            <XCircle size={48} className="mx-auto my-auto text-alert-error" />
          ),
        });
      },
    });
  };

  const editResearcher = (id: number, data: ResearcherPersistence) => {
    researcherEditionMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          toast.success(
            "Researcher editado com sucesso",
            TOAST_SUCCESS_DEFAULT_OPTIONS
          );

          setResearcherToEdit(undefined);
          setFormModalIsOpen(false);
          queryClient.invalidateQueries(["researchers", filters]);
        },
        onError: () => {
          toast.error("Erro ao editar researcher", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            closeButton: false,
            className: "bg-red-50",
            icon: (
              <XCircle size={48} className="mx-auto my-auto text-alert-error" />
            ),
          });
        },
      }
    );
  };

  const handleSaveResearcher = (data: ResearcherPersistence) => {
    if (isFormInEditMode) {
      editResearcher((researcherToEdit as any).id, data);
      return;
    }

    createResearcher(data);
  };

  const handleRemoveResearcher = useCallback(
    (id: number, successAction: () => void) => {
      researcherRemotionMutation.mutate(id, {
        onSuccess: () => {
          toast.success(
            "Researcher deletado com sucesso",
            TOAST_SUCCESS_DEFAULT_OPTIONS
          );

          setFormModalIsOpen(false);
          queryClient.invalidateQueries(["researchers", filters]);
          successAction();
        },
        onError: (error) => {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
        },
      });
    },
    [researcherRemotionMutation]
  );

  const handleSearchResearcher = (debouncedSearchTerm: string) =>
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: debouncedSearchTerm,
    }));

  const handleCloseFormModal = () => {
    if (isFormInEditMode) {
      setResearcherToEdit(undefined);
    }

    setFormModalIsOpen(false);
  };

  const handleCloseResetPasswordModal = () => {
    setResearcherToResetPassword(undefined);
    resetPasswordForm();
    setResetPasswordModalIsOpen(false);
  };

  const handleResetPassword = (values: ResetPasswordFormValues) => {
    if (!researcherToResetPassword) return;

    researcherUpdatePasswordMutation.mutate(
      { id: researcherToResetPassword.id, data: values },
      {
        onSuccess: () => {
          toast.success(
            "Senha do researcher redefinida com sucesso",
            TOAST_SUCCESS_DEFAULT_OPTIONS
          );
          handleCloseResetPasswordModal();
        },
        onError: (error) => {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
        },
      }
    );
  };

  const handleCreateButtonClick = () => {
    if (isFormInEditMode) {
      setResearcherToEdit(undefined);
    }

    setFormModalIsOpen(true);
  };

  useEffect(() => {
    if (isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }));
      return;
    }

    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        take: 20,
        page: 1,
      }));
    }
  }, [isMinScreenWidthMD]);

  return (
    <>
      <div>
        {!isMinScreenWidthMD && <NavigationSelect defaultRoute="researcher" />}

        <div className="md:flex md:justify-between md:items-center md:mb-6">
          <h1 className="mb-4 font-medium text-grey-900 md:text-lg md:mb-0">
            Researcher
          </h1>

          <div className="mb-4 md:flex md:gap-2">
            <SearchInput
              onSearch={handleSearchResearcher}
              placeholder="Buscar por nome ou E-mail"
              className="mb-6 md:w-96 md:mb-0"
            />

            <Can I="create" a="Researcher">
              <Button
                className="h-10 ml-auto"
                onClick={handleCreateButtonClick}
              >
                Cadastrar
              </Button>
            </Can>
          </div>
        </div>

        {isMinScreenWidthMD ? (
          <TableWithManuallyPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <TableWithInfiniteScroll columns={columns} filters={filters} />
        )}
      </div>

      <DialogModal
        isOpen={formModalIsOpen}
        title={`${isFormInEditMode ? "Editar" : "Cadastrar"} researcher`}
        onClose={handleCloseFormModal}
      >
        <ResearcherForm
          isSubmitting={
            researcherCreationMutation.isLoading ||
            researcherEditionMutation.isLoading
          }
          defaultValues={researcherToEdit}
          onSubmit={handleSaveResearcher}
        />
      </DialogModal>

      <DialogModal
        isOpen={resetPasswordModalIsOpen}
        title="Redefinir senha do researcher"
        onClose={handleCloseResetPasswordModal}
      >
        <form
          onSubmit={handleResetPasswordSubmit(handleResetPassword)}
          className="flex flex-col gap-4 p-6"
        >
          {researcherToResetPassword && (
            <p className="text-grey-700">
              Redefinir senha de{" "}
              <strong>{researcherToResetPassword.name}</strong> (
              {researcherToResetPassword.email}).
            </p>
          )}
          <div>
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = "", onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  autoFocus
                  isValid={!invalid}
                  label="Nova senha"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <div>
            <Controller
              name="passwordConfirmation"
              control={control}
              render={({
                field: { value = "", onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label="Confirmar nova senha"
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseResetPasswordModal}
              disabled={researcherUpdatePasswordMutation.isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!resetPasswordFormState.isValid}
              isLoading={researcherUpdatePasswordMutation.isLoading}
            >
              Redefinir senha
            </Button>
          </div>
        </form>
      </DialogModal>
    </>
  );
};
